import { getDetailedGoodsList as axGetDetailedGoodsList, getGoodsRead as axGetGoodsRead } from './goods.axios.service';

/**
 * Сервис-адаптер для работы с товарами (goods).
 * Инкапсулирует вызовы axios-сервисов и маппинг/дополнительную логику.
 */

function normalizeGoodsResponse(res) {
  if (res && typeof res.success !== 'undefined') {
    return res;
  }

  return {
    success: true,
    data: Array.isArray(res) ? res : res?.data || [],
  };
}

function mapModifierToAdditive(modifier, extra = {}) {
  return {
    ...modifier,
    ...extra,
    min: modifier.minimumAmount,
    max: modifier.maximumAmount,
    default: modifier.defaultAmount,
    freeOfChargeAmount: modifier.freeOfChargeAmount,
  };
}

function buildAdditives(product) {
  const directModifiers = Array.isArray(product?.modifiers) ? product.modifiers : [];
  const groupedModifiers = Array.isArray(product?.groupModifiers)
    ? product.groupModifiers.flatMap((group) =>
        (group.modifiers || []).map((modifier) =>
          mapModifierToAdditive(modifier, {
            groupId: group.groupId,
            groupName: group.groupName,
            groupMinimumAmount: group.groupMinimumAmount,
            groupMaximumAmount: group.groupMaximumAmount,
            groupFreeOfChargeAmount: group.groupFreeOfChargeAmount,
          }),
        ),
      )
    : [];

  const uniqueAdditives = new Map();

  [...directModifiers.map((modifier) => mapModifierToAdditive(modifier)), ...groupedModifiers].forEach((modifier) => {
    if (!uniqueAdditives.has(modifier.id)) {
      uniqueAdditives.set(modifier.id, modifier);
    }
  });

  return Array.from(uniqueAdditives.values());
}

function buildPointModifiers(goods = [], pointId) {
  const unique = new Map();

  (Array.isArray(goods) ? goods : []).forEach((product) => {
    const directModifiers = Array.isArray(product?.modifiers) ? product.modifiers : [];
    const groupModifiers = Array.isArray(product?.groupModifiers) ? product.groupModifiers : [];

    directModifiers.forEach((modifier) => {
      if (!modifier?.id) return;

      if (!unique.has(modifier.id)) {
        unique.set(modifier.id, {
          ...modifier,
          pointId,
          modifies: new Set(),
        });
      }
    });

    groupModifiers.forEach((group) => {
      const groupName =
        group?.groupName ??
        group?.name ??
        group?.title ??
        group?.groupTitle ??
        null;
      const modifiers = Array.isArray(group?.modifiers) ? group.modifiers : [];

      modifiers.forEach((modifier) => {
        if (!modifier?.id) return;

        if (!unique.has(modifier.id)) {
          unique.set(modifier.id, {
            ...modifier,
            pointId,
            modifies: new Set(),
          });
        }

        const current = unique.get(modifier.id);
        if (groupName) current.modifies.add(groupName);
      });
    });
  });

  return Array.from(unique.values()).map((item) => ({
    ...item,
    modifies: Array.from(item.modifies || []).sort((a, b) => a.localeCompare(b)),
  }));
}

export function normalizeProductForEdit(product) {
  if (!product) return null;

  return {
    ...product,
    units: product.units || product.unit || null,
    additives: buildAdditives(product),
  };
}

/**
 * Получить детальный список товаров
 * @param {import('./goods.axios.service').GoodsDetailedListBody} data
 * @param {Object} headers
 * @returns {Promise<import('./goods.axios.service').GoodsDetailedListResponse>}
 */
export async function getDetailedGoodsList(data = {}, headers = {}) {
  try {
    const res = await axGetDetailedGoodsList(data, headers);
    return normalizeGoodsResponse(res);
  } catch (error) {
    // В этом слое можно подключить глобальный сервис уведомлений (Toast)
    console.error('goods.service -> getDetailedGoodsList error:', error);
    throw error;
  }
}

/**
 * Получить товары с размерами, модификаторами и группами модификаторов
 * @param {import('./goods.axios.service').GoodsReadBody} data
 * @param {Object} headers
 * @returns {Promise<{success: boolean, data: Array}>}
 */
export async function getGoodsRead(data = {}, headers = {}) {
  try {
    const res = await axGetGoodsRead(data, headers);
    return normalizeGoodsResponse(res);
  } catch (error) {
    console.error('goods.service -> getGoodsRead error:', error);
    throw error;
  }
}

/**
 * Получить один товар по id
 * @param {string} productId
 * @param {string} pointId
 * @param {Object} headers
 * @returns {Promise<Object|null>}
 */
export async function getProductById(productId, pointId, headers = {}) {
  if (!pointId) {
    throw new Error('pointId is required for getProductById');
  }

  const response = await getGoodsRead({ pointId, productIds: [productId] }, headers);
  return normalizeProductForEdit(response.data?.[0] || null);
}

/**
 * Получить список модификаторов (добавок) для мерчанта.
 * Обертка над getDetailedGoodsList, которая передает { type: 'modifier' }.
 * @returns {Promise<import('./goods.axios.service').GoodsItem[]>}
 */
export async function getModifiersList(pointId, headers = {}) {
  try {
    if (!pointId) {
      throw new Error('pointId is required for getModifiersList');
    }

    const response = await getGoodsRead({ pointId, deleted: false }, headers);
    const goods = Array.isArray(response.data) ? response.data : [];
    return buildPointModifiers(goods, pointId);
  } catch (error) {
    console.error('goods.service -> getModifiersList error:', error);
    throw error;
  }
}
